import React, { useState, useEffect } from 'react';
import '../../assets/scss/seasonalDates.scss';

const SeasonalDatesManager = ({ userContext, onSeasonalDatesChange, selectedYear, onYearChange }) => {
    console.log('SeasonalDatesManager props:', { selectedYear, onYearChange: !!onYearChange });

    const [seasonalDates, setSeasonalDates] = useState({
        lowSeason: [{ startMonth: '', startDay: '', endMonth: '', endDay: '' }],
        midSeason: [{ startMonth: '', startDay: '', endMonth: '', endDay: '' }],
        highSeason: [{ startMonth: '', startDay: '', endMonth: '', endDay: '' }]
    });
    const [availableYears, setAvailableYears] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingCurrent, setIsLoadingCurrent] = useState(true);
    const [message, setMessage] = useState('');

    const seasonLabels = {
        lowSeason: 'Basse Saison',
        midSeason: 'Moyenne Saison',
        highSeason: 'Haute Saison'
    };

    const seasonColors = {
        lowSeason: '#28a745',
        midSeason: '#ffc107',
        highSeason: '#dc3545'
    };

    // Load available years and current seasonal dates on component mount
    useEffect(() => {
        const loadData = async () => {
            try {
                console.log('Loading initial seasonal data...');
                // Get all seasons to determine available years
                const allSeasons = await userContext.apiReducer('getAllSeasons');
                console.log('Initial allSeasons response:', allSeasons);

                if (allSeasons) {
                    const years = Object.keys(allSeasons).map(Number).sort((a, b) => a - b);
                    console.log('Available years:', years);
                    setAvailableYears(years);

                    // If no years exist, add current year
                    if (years.length === 0) {
                        const currentYear = new Date().getFullYear();
                        console.log('No years found, using current year:', currentYear);
                        setAvailableYears([currentYear]);
                        if (onYearChange) onYearChange(currentYear);
                    } else {
                        // Use the most recent year or current year if available
                        const currentYear = new Date().getFullYear();
                        const mostRecentYear = years[years.length - 1];
                        const yearToUse = years.includes(currentYear) ? currentYear : mostRecentYear;
                        console.log('Using year:', yearToUse);
                        if (onYearChange) onYearChange(yearToUse);

                        // Load seasonal dates for selected year
                        if (allSeasons[yearToUse]) {
                            console.log('Setting seasonal dates for year:', yearToUse, allSeasons[yearToUse]);
                            setSeasonalDates(allSeasons[yearToUse]);

                            // Notify parent component of loaded data
                            if (onSeasonalDatesChange) {
                                onSeasonalDatesChange(allSeasons[yearToUse]);
                            }
                        }
                    }
                } else {
                    console.log('No allSeasons data received');
                }
            } catch (error) {
                console.error('Error loading seasonal dates:', error);
                // Keep default empty values on error
            } finally {
                setIsLoadingCurrent(false);
            }
        };

        if (userContext?.token) {
            loadData();
        } else {
            setIsLoadingCurrent(false);
        }
    }, [userContext?.token]);

    // Load seasonal dates when year changes
    useEffect(() => {
        const loadSeasonalDatesForYear = async () => {
            if (!userContext?.token || !selectedYear) return;

            console.log('Loading seasonal dates for year:', selectedYear);

            try {
                const allSeasons = await userContext.apiReducer('getAllSeasons');
                console.log('All seasons response:', allSeasons);

                if (allSeasons && allSeasons[selectedYear]) {
                    console.log('Found seasonal dates for year:', selectedYear, allSeasons[selectedYear]);
                    setSeasonalDates(allSeasons[selectedYear]);

                    // Notify parent component of loaded data
                    if (onSeasonalDatesChange) {
                        onSeasonalDatesChange(allSeasons[selectedYear]);
                    }
                } else {
                    console.log('No seasonal dates found for year:', selectedYear);
                    // Reset to empty structure for new year
                    const emptySeasons = {
                        lowSeason: [{ startMonth: '', startDay: '', endMonth: '', endDay: '' }],
                        midSeason: [{ startMonth: '', startDay: '', endMonth: '', endDay: '' }],
                        highSeason: [{ startMonth: '', startDay: '', endMonth: '', endDay: '' }]
                    };
                    setSeasonalDates(emptySeasons);

                    if (onSeasonalDatesChange) {
                        onSeasonalDatesChange(emptySeasons);
                    }
                }
            } catch (error) {
                console.error('Error loading seasonal dates for year:', error);
            }
        };

        loadSeasonalDatesForYear();
    }, [selectedYear, userContext?.token]);

    const handleDateChange = (season, index, field, value) => {
        const newSeasonalDates = {
            ...seasonalDates,
            [season]: seasonalDates[season].map((period, i) =>
                i === index ? { ...period, [field]: value } : period
            )
        };
        setSeasonalDates(newSeasonalDates);

        // Notify parent component of changes
        if (onSeasonalDatesChange) {
            onSeasonalDatesChange(newSeasonalDates);
        }
    };

    const addDateRange = (season) => {
        setSeasonalDates(prev => ({
            ...prev,
            [season]: [...prev[season], { startMonth: '', startDay: '', endMonth: '', endDay: '' }]
        }));
    };

    const removeDateRange = (season, index) => {
        if (seasonalDates[season].length > 1) {
            setSeasonalDates(prev => ({
                ...prev,
                [season]: prev[season].filter((_, i) => i !== index)
            }));
        }
    };

    const validateDates = () => {
        for (const season of Object.keys(seasonalDates)) {
            for (const period of seasonalDates[season]) {
                if (period.startMonth && period.startDay && period.endMonth && period.endDay) {
                    const startMonth = parseInt(period.startMonth);
                    const startDay = parseInt(period.startDay);
                    const endMonth = parseInt(period.endMonth);
                    const endDay = parseInt(period.endDay);

                    // Basic validation for valid month/day ranges
                    if (startMonth < 1 || startMonth > 12 || endMonth < 1 || endMonth > 12) {
                        setMessage(`Erreur: Mois invalide pour ${seasonLabels[season]}`);
                        return false;
                    }
                    if (startDay < 1 || startDay > 31 || endDay < 1 || endDay > 31) {
                        setMessage(`Erreur: Jour invalide pour ${seasonLabels[season]}`);
                        return false;
                    }

                    // Create dates in same year to compare (handle year wrap-around later)
                    const startDate = new Date(selectedYear, startMonth - 1, startDay);
                    const endDate = new Date(selectedYear, endMonth - 1, endDay);

                    // Only validate if not crossing year boundary
                    if (startMonth <= endMonth && startDate >= endDate) {
                        setMessage(`Erreur: La date de début doit être antérieure à la date de fin pour ${seasonLabels[season]}`);
                        return false;
                    }
                }
            }
        }
        return true;
    };

    const applySeasonalDates = async () => {
        if (!validateDates()) return;

        setIsLoading(true);
        setMessage('');

        try {
            // Filter out empty date ranges
            const filteredSeasonalDates = {};
            Object.keys(seasonalDates).forEach(season => {
                filteredSeasonalDates[season] = seasonalDates[season].filter(
                    period => period.startMonth && period.startDay && period.endMonth && period.endDay
                );
            });

            const result = await userContext.apiReducer('setSeasonalDatesForYear', {
                year: selectedYear,
                seasonalDates: filteredSeasonalDates
            });

            if (result && result.message) {
                setMessage(`✅ ${result.message}`);

                // Update available years if this is a new year
                if (!availableYears.includes(selectedYear)) {
                    setAvailableYears(prev => [...prev, selectedYear].sort((a, b) => a - b));
                }
            } else {
                setMessage(`✅ Dates saisonnières sauvegardées pour ${selectedYear}!`);
            }
        } catch (error) {
            console.error('Error applying seasonal dates:', error);
            setMessage(`❌ Erreur: ${error.message || 'Une erreur est survenue lors de la sauvegarde des dates saisonnières'}`);
        } finally {
            setIsLoading(false);
        }
    };

    const months = [
        { value: 1, label: 'Janvier' },
        { value: 2, label: 'Février' },
        { value: 3, label: 'Mars' },
        { value: 4, label: 'Avril' },
        { value: 5, label: 'Mai' },
        { value: 6, label: 'Juin' },
        { value: 7, label: 'Juillet' },
        { value: 8, label: 'Août' },
        { value: 9, label: 'Septembre' },
        { value: 10, label: 'Octobre' },
        { value: 11, label: 'Novembre' },
        { value: 12, label: 'Décembre' }
    ];

    if (isLoadingCurrent) {
        return (
            <div className="seasonal-dates-manager">
                <div className="seasonal-header">
                    <h3 className="blue fw-bold">Configuration des Saisons Globales</h3>
                    <div className="text-center mt-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Chargement...</span>
                        </div>
                        <p className="mt-2 text-muted">Chargement des configurations actuelles...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="seasonal-dates-manager">
            <div className="seasonal-header">
                <h3 className="blue fw-bold">Configuration des Saisons Globales</h3>
                <p className="text-muted">
                    Définissez les périodes de basse, moyenne et haute saison pour chaque année.
                </p>

                {/* Year Selection */}
                <div className="year-selection mb-3">
                    <label htmlFor="yearSelect" className="form-label fw-bold">Année:</label>
                    <div className="d-flex gap-2 align-items-center">
                        <select
                            id="yearSelect"
                            value={selectedYear}
                            onChange={(e) => onYearChange(parseInt(e.target.value))}
                            className="form-select"
                            style={{ width: 'auto' }}
                        >
                            {availableYears.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => {
                                const newYear = selectedYear + 1;
                                if (!availableYears.includes(newYear)) {
                                    setAvailableYears(prev => [...prev, newYear].sort((a, b) => a - b));
                                }
                                onYearChange(newYear);
                            }}
                            className="btn btn-sm btn-outline-primary"
                        >
                            + Nouvelle année
                        </button>
                    </div>
                </div>
            </div>

            {Object.entries(seasonalDates).map(([season, periods]) => (
                <div key={season} className="season-section" style={{ borderColor: seasonColors[season] }}>
                    <div className="season-header">
                        <h4 style={{ color: seasonColors[season] }}>
                            {seasonLabels[season]}
                        </h4>
                        <button
                            type="button"
                            onClick={() => addDateRange(season)}
                            className="btn btn-sm btn-outline-secondary"
                        >
                            + Ajouter une période
                        </button>
                    </div>

                    {periods.map((period, index) => (
                        <div key={index} className="date-range-row">
                            <div className="date-inputs">
                                <div className="date-input-group">
                                    <label>Du:</label>
                                    <div className="month-day-inputs">
                                        <select
                                            value={period.startMonth || ''}
                                            onChange={(e) => handleDateChange(season, index, 'startMonth', e.target.value)}
                                            className="form-control month-select"
                                        >
                                            <option value="">Mois</option>
                                            {months.map(month => (
                                                <option key={month.value} value={month.value}>
                                                    {month.label}
                                                </option>
                                            ))}
                                        </select>
                                        <input
                                            type="number"
                                            min="1"
                                            max="31"
                                            placeholder="Jour"
                                            value={period.startDay || ''}
                                            onChange={(e) => handleDateChange(season, index, 'startDay', e.target.value)}
                                            className="form-control day-input"
                                        />
                                    </div>
                                </div>
                                <div className="date-input-group">
                                    <label>Au:</label>
                                    <div className="month-day-inputs">
                                        <select
                                            value={period.endMonth || ''}
                                            onChange={(e) => handleDateChange(season, index, 'endMonth', e.target.value)}
                                            className="form-control month-select"
                                        >
                                            <option value="">Mois</option>
                                            {months.map(month => (
                                                <option key={month.value} value={month.value}>
                                                    {month.label}
                                                </option>
                                            ))}
                                        </select>
                                        <input
                                            type="number"
                                            min="1"
                                            max="31"
                                            placeholder="Jour"
                                            value={period.endDay || ''}
                                            onChange={(e) => handleDateChange(season, index, 'endDay', e.target.value)}
                                            className="form-control day-input"
                                        />
                                    </div>
                                </div>
                            </div>
                            {periods.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => removeDateRange(season, index)}
                                    className="btn btn-sm btn-outline-danger"
                                >
                                    Supprimer
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            ))}

            <div className="action-section">
                <button
                    type="button"
                    onClick={applySeasonalDates}
                    disabled={isLoading}
                    className="btn btn-primary btn-lg"
                >
                    {isLoading ? 'Sauvegarde en cours...' : `Sauvegarder pour ${selectedYear}`}
                </button>
            </div>

            {message && (
                <div className={`alert ${message.includes('✅') ? 'alert-success' : 'alert-danger'} mt-3`}>
                    {message}
                </div>
            )}
        </div>
    );
};

export default SeasonalDatesManager; 